import { useState, useEffect } from 'react';
import { useMoanaPaye } from '../../contexts/MoanaPayeContext';
import {
  FileText, Download, FileSpreadsheet, BarChart3, Users, BookOpen,
  ClipboardList, TrendingUp, Shield, Calendar, ChevronDown, Loader2,
  AlertTriangle, PieChart
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (v) => v != null ? Number(v).toLocaleString('fr-FR') : '-';
const MOIS_NOMS = ['','Janvier','Fevrier','Mars','Avril','Mai','Juin','Juillet','Aout','Septembre','Octobre','Novembre','Decembre'];

const TABS = [
  { id: 'dashboard', label: 'Tableau de bord', icon: BarChart3 },
  { id: 'livre', label: 'Livre de paie', icon: BookOpen },
  { id: 'od', label: 'OD Salaires', icon: ClipboardList },
  { id: 'charges', label: 'Charges sociales', icon: Shield },
  { id: 'masse', label: 'Masse salariale', icon: TrendingUp },
  { id: 'provisions', label: 'Provisions CP', icon: Calendar },
  { id: 'individuel', label: 'Etats individuels', icon: Users },
  { id: 'registre', label: 'Registre personnel', icon: FileText },
  { id: 'annuel', label: 'Etats annuels', icon: PieChart },
];

function Card({ children, className = '' }) {
  return <div className={`bg-[#1a2332] border border-white/10 rounded-xl ${className}`}>{children}</div>;
}

function StatCard({ label, value, sub, icon: Icon, color = '#2D5A5A' }) {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-slate-400 text-xs">{label}</span>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + '20' }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
      </div>
      <div className="text-xl font-bold text-white">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </Card>
  );
}

function PdfBtn({ href, label = 'PDF' }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" data-testid={`btn-pdf-${label.toLowerCase()}`}
       className="inline-flex items-center gap-1.5 bg-[#2D5A5A] hover:bg-[#3a7272] text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors">
      <FileText className="w-3.5 h-3.5" />{label}
    </a>
  );
}

function XlsBtn({ href, label = 'Excel' }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" data-testid={`btn-xls-${label.toLowerCase()}`}
       className="inline-flex items-center gap-1.5 bg-[#E8A020] hover:bg-[#d4911a] text-[#1a2332] text-xs font-medium px-3 py-1.5 rounded-lg transition-colors">
      <FileSpreadsheet className="w-3.5 h-3.5" />{label}
    </a>
  );
}

function MiniBar({ data, maxKey = 'brut' }) {
  const max = Math.max(...data.map(d => d[maxKey]), 1);
  return (
    <div className="flex items-end gap-1 h-24">
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <div className="w-full bg-[#2D5A5A] rounded-t-sm transition-all"
               style={{ height: `${Math.max((d[maxKey] / max) * 80, 2)}px` }}
               title={`${d.label}: ${fmt(d[maxKey])} F`} />
          <span className="text-[9px] text-slate-500 truncate w-full text-center">{d.label?.slice(0, 3)}</span>
        </div>
      ))}
    </div>
  );
}

export default function ReportingPage() {
  const { mpFetch, token } = useMoanaPaye();
  const [entreprises, setEntreprises] = useState([]);
  const [selectedEnt, setSelectedEnt] = useState('');
  const [mois, setMois] = useState(new Date().getMonth() + 1);
  const [annee, setAnnee] = useState(2026);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [tabOpen, setTabOpen] = useState(false);

  useEffect(() => {
    mpFetch('/api/moana-paye/entreprises').then(d => {
      setEntreprises(d);
      if (d.length) setSelectedEnt(d[0].id);
    }).catch(console.error);
  }, []);

  const loadTab = async (tab, eid, m, a) => {
    if (!eid) return;
    setLoading(true);
    setData(null);
    try {
      let result;
      switch (tab) {
        case 'dashboard':
          result = await mpFetch(`/api/moana-paye/reporting/${eid}/tableau-bord`);
          break;
        case 'livre':
          // Livre de paie only has PDF/XLS endpoints, no JSON data to fetch
          result = { ready: true };
          break;
        case 'od':
        case 'charges':
        case 'masse':
        case 'provisions':
          const paths = {
            od: 'od-salaires', charges: 'charges-sociales',
            masse: 'masse-salariale', provisions: 'provisions-conges'
          };
          result = await mpFetch(`/api/moana-paye/reporting/${eid}/${paths[tab]}/${a}/${m}`);
          break;
        case 'registre':
          result = await mpFetch(`/api/moana-paye/reporting/${eid}/registre-personnel`);
          break;
        case 'annuel':
          result = await mpFetch(`/api/moana-paye/reporting/${eid}/etats-annuels/${a}`);
          break;
        case 'individuel':
          result = await mpFetch(`/api/moana-paye/reporting/${eid}/salaries-list`);
          break;
        default:
          break;
      }
      setData(result);
    } catch (err) {
      setData({ error: err.message });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (selectedEnt) loadTab(activeTab, selectedEnt, mois, annee);
  }, [selectedEnt, activeTab, mois, annee]);

  const authUrl = (path) => `${API}${path}?token=${token}`;
  const baseUrl = `${API}/api/moana-paye/reporting/${selectedEnt}`;

  const selectCls = "bg-[#0f1520] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#2D5A5A]";

  return (
    <div data-testid="mp-reporting">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h2 className="text-xl font-bold text-white">Reporting</h2>
        <div className="flex items-center gap-2 flex-wrap">
          {entreprises.length > 1 && (
            <select value={selectedEnt} onChange={e => setSelectedEnt(e.target.value)} className={selectCls} data-testid="mp-report-ent-select">
              {entreprises.map(e => <option key={e.id} value={e.id}>{e.raison_sociale}</option>)}
            </select>
          )}
          {!['dashboard', 'registre'].includes(activeTab) && (
            <>
              <select value={mois} onChange={e => setMois(parseInt(e.target.value))} className={selectCls} data-testid="mp-report-mois-select">
                {MOIS_NOMS.slice(1).map((n, i) => <option key={i + 1} value={i + 1}>{n}</option>)}
              </select>
              <input type="number" value={annee} onChange={e => setAnnee(parseInt(e.target.value))} className={`${selectCls} w-20`} data-testid="mp-report-annee-input" />
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-5">
        {/* Mobile dropdown */}
        <div className="lg:hidden">
          <button onClick={() => setTabOpen(!tabOpen)} className="w-full flex items-center justify-between bg-[#1a2332] border border-white/10 rounded-xl px-4 py-3 text-white text-sm" data-testid="mp-report-tab-mobile">
            <span className="flex items-center gap-2">
              {(() => { const T = TABS.find(t => t.id === activeTab); return T ? <><T.icon className="w-4 h-4" />{T.label}</> : null; })()}
            </span>
            <ChevronDown className={`w-4 h-4 transition-transform ${tabOpen ? 'rotate-180' : ''}`} />
          </button>
          {tabOpen && (
            <div className="mt-1 bg-[#1a2332] border border-white/10 rounded-xl overflow-hidden">
              {TABS.map(t => (
                <button key={t.id} onClick={() => { setActiveTab(t.id); setTabOpen(false); }}
                  className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${activeTab === t.id ? 'bg-[#2D5A5A] text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
                  <t.icon className="w-4 h-4" />{t.label}
                </button>
              ))}
            </div>
          )}
        </div>
        {/* Desktop tabs */}
        <div className="hidden lg:flex gap-1 bg-[#1a2332] border border-white/10 rounded-xl p-1 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`mp-report-tab-${t.id}`}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                activeTab === t.id ? 'bg-[#2D5A5A] text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}>
              <t.icon className="w-3.5 h-3.5" />{t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-[#2D5A5A]" />
          <span className="ml-2 text-slate-400 text-sm">Chargement...</span>
        </div>
      ) : data?.error ? (
        <Card className="p-8 text-center">
          <AlertTriangle className="w-8 h-8 text-[#E8A020] mx-auto mb-2" />
          <p className="text-slate-400 text-sm">{data.error}</p>
        </Card>
      ) : (
        <div>
          {activeTab === 'dashboard' && data && <DashboardView data={data} baseUrl={baseUrl} />}
          {activeTab === 'livre' && data && <LivreView data={data} baseUrl={baseUrl} mois={mois} annee={annee} />}
          {activeTab === 'od' && data && <OdView data={data} baseUrl={baseUrl} mois={mois} annee={annee} />}
          {activeTab === 'charges' && data && <ChargesView data={data} baseUrl={baseUrl} mois={mois} annee={annee} />}
          {activeTab === 'masse' && data && <MasseView data={data} baseUrl={baseUrl} mois={mois} annee={annee} />}
          {activeTab === 'provisions' && data && <ProvisionsView data={data} baseUrl={baseUrl} mois={mois} annee={annee} />}
          {activeTab === 'individuel' && data && <IndividuelView salaries={data} selectedEnt={selectedEnt} annee={annee} mpFetch={mpFetch} baseUrl={baseUrl} />}
          {activeTab === 'registre' && data && <RegistreView data={data} baseUrl={baseUrl} />}
          {activeTab === 'annuel' && data && <AnnuelView data={data} baseUrl={baseUrl} annee={annee} />}
        </div>
      )}
    </div>
  );
}

/* ========= SUB-VIEWS ========= */

function DashboardView({ data, baseUrl }) {
  const d = data;
  const declStatus = (type) => {
    const decl = d.declarations?.find(x => x.type_declaration === type);
    return decl ? decl.statut : 'non générée';
  };
  const statusBadge = (s) => {
    if (s === 'transmise') return <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/15 text-green-400">Transmise</span>;
    if (s === 'generee') return <span className="text-xs px-2 py-0.5 rounded-full bg-[#E8A020]/15 text-[#E8A020]">Generee</span>;
    return <span className="text-xs px-2 py-0.5 rounded-full bg-slate-500/15 text-slate-400">Non generee</span>;
  };

  return (
    <div data-testid="mp-report-dashboard">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <StatCard label="Salaries actifs" value={d.nb_salaries} icon={Users} color="#2D5A5A" />
        <StatCard label="Masse salariale brute" value={`${fmt(d.masse_salariale_brute)} F`} icon={TrendingUp} color="#E8A020" />
        <StatCard label="CDD a echeance" value={d.alertes?.cdd_echeance ?? 0} icon={AlertTriangle} color={d.alertes?.cdd_echeance > 0 ? '#ef4444' : '#22c55e'} />
        <StatCard label="Conges a approuver" value={d.alertes?.conges_a_approuver ?? 0} icon={Calendar} color={d.alertes?.conges_a_approuver > 0 ? '#E8A020' : '#22c55e'} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4">
          <h3 className="text-white font-semibold text-sm mb-2">Periode en cours</h3>
          {d.periode_actuelle ? (
            <div className="flex items-center gap-3">
              <span className="text-white text-lg font-bold">{MOIS_NOMS[d.periode_actuelle.mois]} {d.periode_actuelle.annee}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                d.periode_actuelle.statut === 'cloturee' ? 'bg-green-500/15 text-green-400' :
                d.periode_actuelle.statut === 'ouverte' ? 'bg-blue-500/15 text-blue-400' : 'bg-[#E8A020]/15 text-[#E8A020]'
              }`}>{d.periode_actuelle.statut}</span>
            </div>
          ) : <p className="text-slate-500 text-sm">Aucune periode</p>}
        </Card>

        <Card className="p-4">
          <h3 className="text-white font-semibold text-sm mb-2">Declarations du mois</h3>
          <div className="space-y-2">
            {['dmo', 'cst_4010', 'annexe_4010a'].map(type => (
              <div key={type} className="flex items-center justify-between">
                <span className="text-slate-400 text-xs">{type === 'dmo' ? 'DMO (CPS)' : type === 'cst_4010' ? 'CST 4010 (DICP)' : 'Annexe 4010-A'}</span>
                {statusBadge(declStatus(type))}
              </div>
            ))}
          </div>
        </Card>
      </div>

      {d.evolution_12m?.length > 0 && (
        <Card className="p-4 mt-4">
          <h3 className="text-white font-semibold text-sm mb-3">Evolution masse salariale (12 mois)</h3>
          <MiniBar data={d.evolution_12m} />
        </Card>
      )}
    </div>
  );
}

function LivreView({ data, baseUrl, mois, annee }) {
  return (
    <div data-testid="mp-report-livre">
      <div className="flex items-center gap-2 mb-4">
        <PdfBtn href={`${baseUrl}/livre-paie/${annee}/${mois}`} label="PDF" />
        <XlsBtn href={`${baseUrl}/livre-paie/${annee}/${mois}/xls`} label="Excel" />
      </div>
      {data?.error ? (
        <Card className="p-6 text-center"><p className="text-slate-400 text-sm">{data.error}</p></Card>
      ) : (
        <p className="text-slate-400 text-sm">Cliquez sur PDF ou Excel pour telecharger le livre de paie de {MOIS_NOMS[mois]} {annee}.</p>
      )}
    </div>
  );
}

function OdView({ data, baseUrl, mois, annee }) {
  if (!data?.lignes) return <Card className="p-6 text-center"><p className="text-slate-400 text-sm">Aucune ecriture pour cette periode.</p></Card>;
  return (
    <div data-testid="mp-report-od">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <PdfBtn href={`${baseUrl}/od-salaires/${annee}/${mois}/pdf`} label="PDF" />
        <XlsBtn href={`${baseUrl}/od-salaires/${annee}/${mois}/xls`} label="Excel" />
        <span className="text-slate-500 text-xs ml-2">Ref: {data.ref} — {data.nb_ecritures} ecritures</span>
      </div>
      <Card className="overflow-hidden">
        <table className="w-full text-sm" data-testid="mp-od-table">
          <thead>
            <tr className="bg-[#2D5A5A]/30 text-left">
              <th className="px-4 py-2 text-slate-300 font-medium text-xs">Date</th>
              <th className="px-4 py-2 text-slate-300 font-medium text-xs">Cpt Debit</th>
              <th className="px-4 py-2 text-slate-300 font-medium text-xs">Cpt Credit</th>
              <th className="px-4 py-2 text-slate-300 font-medium text-xs">Libelle</th>
              <th className="px-4 py-2 text-slate-300 font-medium text-xs text-right">Montant (F)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {data.lignes.map((l, i) => (
              <tr key={i} className="hover:bg-white/3">
                <td className="px-4 py-2 text-slate-400 text-xs">{l.date}</td>
                <td className="px-4 py-2 text-white text-xs font-mono">{l.compte_debit}</td>
                <td className="px-4 py-2 text-white text-xs font-mono">{l.compte_credit}</td>
                <td className="px-4 py-2 text-slate-300 text-xs">{l.libelle}</td>
                <td className="px-4 py-2 text-white text-xs text-right font-medium">{fmt(l.montant)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-[#2D5A5A]/10 border-t border-white/10">
              <td colSpan={4} className="px-4 py-2 text-[#2D5A5A] font-bold text-xs">TOTAL</td>
              <td className="px-4 py-2 text-[#E8A020] font-bold text-xs text-right">{fmt(data.total_debit)}</td>
            </tr>
          </tfoot>
        </table>
      </Card>
    </div>
  );
}

function ChargesView({ data, baseUrl, mois, annee }) {
  if (!data?.charges) return null;
  const Section = ({ title, items, color }) => (
    <div className="mb-4">
      <h4 className="text-white font-semibold text-xs mb-2 flex items-center gap-2">
        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />{title}
      </h4>
      <div className="space-y-1">
        {Object.entries(items).sort().map(([k, v]) => (
          <div key={k} className="flex items-center justify-between px-3 py-1.5 bg-white/3 rounded-lg">
            <span className="text-slate-400 text-xs">{k}</span>
            <span className="text-white text-xs font-medium">{fmt(v)} F</span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div data-testid="mp-report-charges">
      <div className="flex items-center gap-2 mb-4">
        <PdfBtn href={`${baseUrl}/charges-sociales/${annee}/${mois}/pdf`} label="PDF" />
        <span className="text-slate-500 text-xs ml-2">{data.nb_salaries} salarie(s)</span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4">
          <Section title="Cotisations salariales CPS" items={data.charges.salariales} color="#2D5A5A" />
          <Section title="CST (Contribution Solidarite Territoriale)" items={data.charges.cst} color="#E8A020" />
        </Card>
        <Card className="p-4">
          <Section title="Cotisations patronales CPS" items={data.charges.patronales} color="#6366f1" />
        </Card>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
        <StatCard label="Total salariales" value={`${fmt(data.total_salariales)} F`} icon={Users} color="#2D5A5A" />
        <StatCard label="Total patronales" value={`${fmt(data.total_patronales)} F`} icon={Shield} color="#6366f1" />
        <StatCard label="Total general" value={`${fmt(data.total_general)} F`} icon={BarChart3} color="#E8A020" />
      </div>
    </div>
  );
}

function MasseView({ data, baseUrl, mois, annee }) {
  if (!data) return null;
  const evoPct = data.evolution_pct;
  return (
    <div data-testid="mp-report-masse">
      <div className="flex items-center gap-2 mb-4">
        <PdfBtn href={`${baseUrl}/masse-salariale/${annee}/${mois}/pdf`} label="PDF" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <StatCard label="Effectif" value={data.nb} icon={Users} color="#2D5A5A" />
        <StatCard label="Masse brute" value={`${fmt(data.brut)} F`} icon={TrendingUp} color="#E8A020" />
        <StatCard label="Masse nette" value={`${fmt(data.net)} F`} icon={TrendingUp} color="#22c55e" />
        <StatCard label="Cout total employeur" value={`${fmt(data.cout_total)} F`} icon={BarChart3} color="#ef4444"
                  sub={evoPct !== 0 ? `${evoPct > 0 ? '+' : ''}${evoPct}% vs mois prec.` : undefined} />
      </div>
      <Card className="p-4">
        <h3 className="text-white font-semibold text-sm mb-3">Repartition par statut</h3>
        <div className="space-y-2">
          {Object.entries(data.par_statut || {}).map(([st, v]) => {
            const pct = data.brut > 0 ? (v.brut / data.brut * 100).toFixed(1) : 0;
            return (
              <div key={st} className="flex items-center gap-3">
                <span className="text-slate-400 text-xs w-16">{st}</span>
                <div className="flex-1 h-5 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-[#2D5A5A] rounded-full transition-all" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-white text-xs font-medium w-28 text-right">{fmt(v.brut)} F ({pct}%)</span>
                <span className="text-slate-500 text-xs w-16 text-right">{v.nb} sal.</span>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

function ProvisionsView({ data, baseUrl, mois, annee }) {
  if (!data?.provisions) return null;
  return (
    <div data-testid="mp-report-provisions">
      <div className="flex items-center gap-2 mb-4">
        <PdfBtn href={`${baseUrl}/provisions-conges/${annee}/${mois}`} label="PDF" />
        <XlsBtn href={`${baseUrl}/provisions-conges/${annee}/${mois}/xls`} label="Excel" />
      </div>
      <Card className="overflow-hidden">
        <table className="w-full text-sm" data-testid="mp-provisions-table">
          <thead>
            <tr className="bg-[#2D5A5A]/30 text-left">
              <th className="px-4 py-2 text-slate-300 font-medium text-xs">Salarie</th>
              <th className="px-4 py-2 text-slate-300 font-medium text-xs text-right">Brut mois</th>
              <th className="px-4 py-2 text-slate-300 font-medium text-xs text-right">Jrs acquis</th>
              <th className="px-4 py-2 text-slate-300 font-medium text-xs text-right">Prov. brute</th>
              <th className="px-4 py-2 text-slate-300 font-medium text-xs text-right">Charges pat.</th>
              <th className="px-4 py-2 text-slate-300 font-medium text-xs text-right">Prov. totale</th>
              <th className="px-4 py-2 text-slate-300 font-medium text-xs text-right">Solde CP</th>
              <th className="px-4 py-2 text-slate-300 font-medium text-xs text-right">Valeur solde</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {data.provisions.map((p, i) => (
              <tr key={i} className="hover:bg-white/3">
                <td className="px-4 py-2 text-white text-xs font-medium">{p.nom}</td>
                <td className="px-4 py-2 text-slate-300 text-xs text-right">{fmt(p.brut)}</td>
                <td className="px-4 py-2 text-slate-300 text-xs text-right">{p.jours_acquis_mois}</td>
                <td className="px-4 py-2 text-slate-300 text-xs text-right">{fmt(p.provision_brute)}</td>
                <td className="px-4 py-2 text-slate-300 text-xs text-right">{fmt(p.charges_patronales)}</td>
                <td className="px-4 py-2 text-[#E8A020] text-xs text-right font-medium">{fmt(p.provision_totale)}</td>
                <td className="px-4 py-2 text-slate-300 text-xs text-right">{p.solde_conges} j</td>
                <td className="px-4 py-2 text-slate-300 text-xs text-right">{fmt(p.valeur_solde)} F</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-[#2D5A5A]/10 border-t border-white/10">
              <td className="px-4 py-2 text-[#2D5A5A] font-bold text-xs">TOTAL ({data.nb_salaries} sal.)</td>
              <td colSpan={4} />
              <td className="px-4 py-2 text-[#E8A020] font-bold text-xs text-right">{fmt(data.total_provision)} F</td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        </table>
      </Card>
    </div>
  );
}

function IndividuelView({ salaries, selectedEnt, annee, mpFetch, baseUrl }) {
  const [selectedSal, setSelectedSal] = useState(null);
  const [etat, setEtat] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadEtat = async (sid) => {
    setSelectedSal(sid);
    setLoading(true);
    try {
      const res = await mpFetch(`/api/moana-paye/reporting/${selectedEnt}/etat-individuel/${sid}/${annee}`);
      setEtat(res);
    } catch (err) { setEtat({ error: err.message }); }
    setLoading(false);
  };

  if (!Array.isArray(salaries)) return <Card className="p-6 text-center"><p className="text-slate-400 text-sm">Erreur chargement salaries.</p></Card>;

  return (
    <div data-testid="mp-report-individuel">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-4 lg:col-span-1">
          <h3 className="text-white font-semibold text-sm mb-3">Salaries</h3>
          <div className="space-y-1 max-h-96 overflow-y-auto">
            {salaries.map(s => (
              <button key={s.id} onClick={() => loadEtat(s.id)} data-testid={`mp-sal-${s.id}`}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  selectedSal === s.id ? 'bg-[#2D5A5A] text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}>
                <div className="font-medium">{s.nom} {s.prenom}</div>
                <div className="text-xs opacity-60">{s.statut} {!s.actif && '(sorti)'}</div>
              </button>
            ))}
          </div>
        </Card>

        <div className="lg:col-span-2">
          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="w-5 h-5 animate-spin text-[#2D5A5A]" /></div>
          ) : etat?.error ? (
            <Card className="p-6 text-center"><p className="text-slate-400 text-sm">{etat.error}</p></Card>
          ) : etat ? (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <PdfBtn href={`${baseUrl}/etat-individuel/${selectedSal}/${annee}/pdf`} label="PDF" />
              </div>
              <Card className="p-4 mb-4">
                <h3 className="text-white font-semibold text-sm mb-2">{etat.salarie.nom} {etat.salarie.prenom}</h3>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  <div><span className="text-slate-500">Poste:</span> <span className="text-slate-300">{etat.salarie.poste_occupe}</span></div>
                  <div><span className="text-slate-500">Statut:</span> <span className="text-slate-300">{etat.salarie.statut}</span></div>
                  <div><span className="text-slate-500">CPS:</span> <span className="text-slate-300">{etat.salarie.numero_cps}</span></div>
                  <div><span className="text-slate-500">Entree:</span> <span className="text-slate-300">{etat.salarie.date_entree}</span></div>
                </div>
              </Card>
              <Card className="overflow-hidden mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#2D5A5A]/30 text-left">
                      <th className="px-3 py-2 text-slate-300 font-medium text-xs">Mois</th>
                      <th className="px-3 py-2 text-slate-300 font-medium text-xs text-right">Brut</th>
                      <th className="px-3 py-2 text-slate-300 font-medium text-xs text-right">Cot. sal.</th>
                      <th className="px-3 py-2 text-slate-300 font-medium text-xs text-right">CST</th>
                      <th className="px-3 py-2 text-slate-300 font-medium text-xs text-right">Net paye</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {etat.cumuls.mois.map(m => (
                      <tr key={m.mois} className="hover:bg-white/3">
                        <td className="px-3 py-2 text-slate-400 text-xs">{m.label}</td>
                        <td className="px-3 py-2 text-white text-xs text-right">{fmt(m.brut)}</td>
                        <td className="px-3 py-2 text-slate-300 text-xs text-right">{fmt(m.cot_sal)}</td>
                        <td className="px-3 py-2 text-slate-300 text-xs text-right">{fmt(m.cst)}</td>
                        <td className="px-3 py-2 text-[#E8A020] text-xs text-right font-medium">{fmt(m.net)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-[#2D5A5A]/10 border-t border-white/10">
                      <td className="px-3 py-2 text-[#2D5A5A] font-bold text-xs">CUMULS {annee}</td>
                      <td className="px-3 py-2 text-white font-bold text-xs text-right">{fmt(etat.cumuls.brut)}</td>
                      <td className="px-3 py-2 text-slate-300 font-bold text-xs text-right">{fmt(etat.cumuls.cot_sal)}</td>
                      <td className="px-3 py-2 text-slate-300 font-bold text-xs text-right">{fmt(etat.cumuls.cst)}</td>
                      <td className="px-3 py-2 text-[#E8A020] font-bold text-xs text-right">{fmt(etat.cumuls.net)}</td>
                    </tr>
                  </tfoot>
                </table>
              </Card>
              {etat.solde_conges && (
                <Card className="p-4">
                  <h4 className="text-white font-semibold text-xs mb-2">Etat des conges</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                    <div className="bg-white/5 rounded-lg p-2 text-center">
                      <div className="text-slate-500">Acquis N</div>
                      <div className="text-white font-bold">{etat.solde_conges.acquis_n} j</div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-2 text-center">
                      <div className="text-slate-500">Pris N</div>
                      <div className="text-white font-bold">{etat.solde_conges.pris_n} j</div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-2 text-center">
                      <div className="text-slate-500">Report N-1</div>
                      <div className="text-white font-bold">{etat.solde_conges.acquis_n1} j</div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-2 text-center">
                      <div className="text-slate-500">Anciennete</div>
                      <div className="text-white font-bold">{etat.solde_conges.conges_anciennete} j</div>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          ) : (
            <Card className="p-8 text-center">
              <Users className="w-8 h-8 text-slate-600 mx-auto mb-2" />
              <p className="text-slate-400 text-sm">Selectionnez un salarie pour voir son etat individuel</p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function RegistreView({ data, baseUrl }) {
  if (!Array.isArray(data)) return <Card className="p-6 text-center"><p className="text-slate-400 text-sm">Aucun salarie dans le registre.</p></Card>;
  return (
    <div data-testid="mp-report-registre">
      <div className="flex items-center gap-2 mb-4">
        <PdfBtn href={`${baseUrl}/registre-personnel/pdf`} label="PDF" />
        <span className="text-slate-500 text-xs ml-2">{data.length} salarie(s)</span>
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]" data-testid="mp-registre-table">
          <thead>
            <tr className="bg-[#2D5A5A]/30 text-left">
              <th className="px-3 py-2 text-slate-300 font-medium text-xs">N.</th>
              <th className="px-3 py-2 text-slate-300 font-medium text-xs">Nom Prenom</th>
              <th className="px-3 py-2 text-slate-300 font-medium text-xs">Naissance</th>
              <th className="px-3 py-2 text-slate-300 font-medium text-xs">N. CPS</th>
              <th className="px-3 py-2 text-slate-300 font-medium text-xs">Contrat</th>
              <th className="px-3 py-2 text-slate-300 font-medium text-xs">Entree</th>
              <th className="px-3 py-2 text-slate-300 font-medium text-xs">Poste</th>
              <th className="px-3 py-2 text-slate-300 font-medium text-xs">Statut</th>
              <th className="px-3 py-2 text-slate-300 font-medium text-xs">Sortie</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {data.map((r, i) => (
              <tr key={i} className={`hover:bg-white/3 ${!r.actif ? 'opacity-60' : ''}`}>
                <td className="px-3 py-2 text-white text-xs">{r.numero_ordre || i + 1}</td>
                <td className="px-3 py-2 text-white text-xs font-medium">{r.nom} {r.prenom}</td>
                <td className="px-3 py-2 text-slate-400 text-xs">{r.date_naissance || '-'}</td>
                <td className="px-3 py-2 text-slate-400 text-xs">{r.numero_cps || '-'}</td>
                <td className="px-3 py-2 text-slate-400 text-xs">{r.type_contrat || '-'}</td>
                <td className="px-3 py-2 text-slate-400 text-xs">{r.date_entree || '-'}</td>
                <td className="px-3 py-2 text-slate-300 text-xs">{r.poste_occupe || '-'}</td>
                <td className="px-3 py-2 text-slate-400 text-xs">{r.statut || '-'}</td>
                <td className="px-3 py-2 text-xs">
                  {!r.actif ? <span className="text-red-400">{r.date_sortie || '-'} ({r.motif_sortie || '-'})</span> : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function AnnuelView({ data, baseUrl, annee }) {
  if (!data?.mois) return null;
  return (
    <div data-testid="mp-report-annuel">
      <div className="flex items-center gap-2 mb-4">
        <PdfBtn href={`${baseUrl}/etats-annuels/${annee}/pdf`} label="PDF Recapitulatif" />
        <span className="text-slate-500 text-xs ml-2">
          {data.mouvements?.entrees ?? 0} entrees / {data.mouvements?.sorties ?? 0} sorties en {annee}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <StatCard label="Masse brute annuelle" value={`${fmt(data.totaux.brut)} F`} icon={TrendingUp} color="#2D5A5A" />
        <StatCard label="Masse nette annuelle" value={`${fmt(data.totaux.net)} F`} icon={TrendingUp} color="#22c55e" />
        <StatCard label="Cout total annuel" value={`${fmt(data.totaux.cout_total)} F`} icon={BarChart3} color="#E8A020" />
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]" data-testid="mp-annuel-table">
          <thead>
            <tr className="bg-[#2D5A5A]/30 text-left">
              <th className="px-3 py-2 text-slate-300 font-medium text-xs">Mois</th>
              <th className="px-3 py-2 text-slate-300 font-medium text-xs text-right">Effectif</th>
              <th className="px-3 py-2 text-slate-300 font-medium text-xs text-right">Brut</th>
              <th className="px-3 py-2 text-slate-300 font-medium text-xs text-right">Cot. sal.</th>
              <th className="px-3 py-2 text-slate-300 font-medium text-xs text-right">CST</th>
              <th className="px-3 py-2 text-slate-300 font-medium text-xs text-right">Net paye</th>
              <th className="px-3 py-2 text-slate-300 font-medium text-xs text-right">Cot. pat.</th>
              <th className="px-3 py-2 text-slate-300 font-medium text-xs text-right">Cout total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {data.mois.map(m => (
              <tr key={m.mois} className={`hover:bg-white/3 ${m.nb === 0 ? 'opacity-40' : ''}`}>
                <td className="px-3 py-2 text-slate-400 text-xs">{m.label}</td>
                <td className="px-3 py-2 text-white text-xs text-right">{m.nb}</td>
                <td className="px-3 py-2 text-white text-xs text-right">{fmt(m.brut)}</td>
                <td className="px-3 py-2 text-slate-300 text-xs text-right">{fmt(m.cot_sal)}</td>
                <td className="px-3 py-2 text-slate-300 text-xs text-right">{fmt(m.cst)}</td>
                <td className="px-3 py-2 text-[#E8A020] text-xs text-right font-medium">{fmt(m.net)}</td>
                <td className="px-3 py-2 text-slate-300 text-xs text-right">{fmt(m.cot_pat)}</td>
                <td className="px-3 py-2 text-white text-xs text-right">{fmt(m.cout)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-[#2D5A5A]/10 border-t border-white/10">
              <td className="px-3 py-2 text-[#2D5A5A] font-bold text-xs">TOTAUX</td>
              <td className="px-3 py-2 text-white font-bold text-xs text-right">{data.totaux.nb_bulletins}</td>
              <td className="px-3 py-2 text-white font-bold text-xs text-right">{fmt(data.totaux.brut)}</td>
              <td className="px-3 py-2 text-slate-300 font-bold text-xs text-right">{fmt(data.totaux.cot_sal)}</td>
              <td className="px-3 py-2 text-slate-300 font-bold text-xs text-right">{fmt(data.totaux.cst)}</td>
              <td className="px-3 py-2 text-[#E8A020] font-bold text-xs text-right">{fmt(data.totaux.net)}</td>
              <td className="px-3 py-2 text-slate-300 font-bold text-xs text-right">{fmt(data.totaux.cot_pat)}</td>
              <td className="px-3 py-2 text-white font-bold text-xs text-right">{fmt(data.totaux.cout_total)}</td>
            </tr>
          </tfoot>
        </table>
      </Card>
      {data.mois.some(m => m.nb > 0) && (
        <Card className="p-4 mt-4">
          <h3 className="text-white font-semibold text-sm mb-3">Evolution mensuelle {annee}</h3>
          <MiniBar data={data.mois.filter(m => m.nb > 0)} />
        </Card>
      )}
    </div>
  );
}
